import type { BeatNode, OptionNode, OutcomeNode } from './chainTree';
import type { Tone } from './types';

/**
 * Lays the path tree out for the hand-built SVG branch preview (no graph library: depth ≤ 4 is small). Left to right in columns
 * (beat → option → outcome → next beat …); every leaf outcome takes one row, and a parent sits at the middle of its children's rows.
 */

export type LayoutNode = {
  id: string;
  kind: 'beat' | 'option' | 'outcome';
  label: string;
  /** Column (0, 1, 2 for the first beat, option, outcome; +3 per depth) and row (in row units, may be fractional). */
  col: number;
  row: number;
  x: number;
  y: number;
  tone?: Tone | '';
  /** Red: over the depth limit, a cycle, a missing beat or outcome. */
  bad?: boolean;
  /** Flagged option: no good or windfall path and no tone note. */
  flag?: boolean;
  /** Probability shown on the edge into this node (0..1). */
  p?: number;
  reach: number;
};

export type LayoutEdge = { from: string; to: string; label: string };

export type BranchLayout = {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  width: number;
  height: number;
  rows: number;
  cols: number;
};

export const LAYOUT = { colW: 132, rowH: 30, padX: 12, padY: 16, nodeW: 116 } as const;

export const pct = (p: number) => `${Math.round(p * 100)}%`;

export function layoutBranchTree(root: BeatNode | undefined): BranchLayout {
  const nodes: LayoutNode[] = [];
  const edges: LayoutEdge[] = [];
  let nextRow = 0;
  let maxCol = 0;

  const place = (n: Omit<LayoutNode, 'x' | 'y'>) => {
    maxCol = Math.max(maxCol, n.col);
    const full: LayoutNode = {
      ...n,
      x: LAYOUT.padX + n.col * LAYOUT.colW,
      y: LAYOUT.padY + n.row * LAYOUT.rowH,
    };
    nodes.push(full);
    return full;
  };

  const doOutcome = (o: OutcomeNode, col: number): number => {
    let row: number;
    if (o.next) {
      row = doBeat(o.next, col + 1);
    } else {
      row = nextRow++;
    }
    place({
      id: `o:${o.key}`,
      kind: 'outcome',
      label: o.outcomeId || '(none)',
      col,
      row,
      tone: o.tone,
      bad: o.missing,
      p: o.p,
      reach: o.reach,
    });
    if (o.next) edges.push({ from: `o:${o.key}`, to: `b:${o.next.key}`, label: '' });
    return row;
  };

  const doOption = (o: OptionNode, col: number): number => {
    const rows = o.outcomes.map((x) => doOutcome(x, col + 1));
    const row = rows.length ? (rows[0]! + rows[rows.length - 1]!) / 2 : nextRow++;
    place({
      id: `p:${o.key}`,
      kind: 'option',
      label: o.cutscene ? '→' : `${o.optionId}${o.table ? ` → ${o.table}` : ''}`,
      col,
      row,
      flag: !o.cutscene && !o.hasGoodPath && !o.toneNote,
      reach: o.reach,
    });
    for (const x of o.outcomes)
      edges.push({ from: `p:${o.key}`, to: `o:${x.key}`, label: pct(x.p) });
    return row;
  };

  const doBeat = (b: BeatNode, col: number): number => {
    const rows = b.options.map((o) => doOption(o, col + 1));
    const row = rows.length ? (rows[0]! + rows[rows.length - 1]!) / 2 : nextRow++;
    place({
      id: `b:${b.key}`,
      kind: 'beat',
      label: `${b.beatId} · d${b.depth}`,
      col,
      row,
      bad: b.missing || b.cycle || b.tooDeep,
      reach: b.reach,
    });
    for (const o of b.options)
      edges.push({ from: `b:${b.key}`, to: `p:${o.key}`, label: pct(o.reach) });
    return row;
  };

  if (root) doBeat(root, 0);
  const rows = Math.max(1, nextRow);
  return {
    nodes,
    edges,
    rows,
    cols: maxCol + 1,
    width: LAYOUT.padX * 2 + maxCol * LAYOUT.colW + LAYOUT.nodeW,
    height: LAYOUT.padY * 2 + (rows - 1) * LAYOUT.rowH + 12,
  };
}
